import { createTranslator, type Translator } from '@orbind/core'
import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { api, getStoredLocale, setStoredLocale } from './api.ts'

type I18nValue = {
  t: Translator
  locale: string
  setLocale: (locale: string) => void
  ready: boolean
}

const I18nContext = createContext<I18nValue | null>(null)

/**
 * Carrega o bundle ja resolvido pelo servidor.
 *
 * A cadeia de fallback (usuario -> padrao da empresa -> pt-BR -> chave) roda no
 * backend: se cada plataforma resolvesse por conta propria, web, mobile e
 * desktop divergiriam na primeira chave faltante.
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = getStoredLocale()

  const { data, isPending } = useQuery({
    queryKey: ['i18n', locale],
    queryFn: async () => {
      const response = await api.i18n.bundle[':code'].$get({ param: { code: locale } })
      return response.json()
    },
    // Traducao muda por acao de admin, nao por navegacao.
    staleTime: 30 * 60 * 1000,
  })

  const value = useMemo<I18nValue>(() => {
    const catalog = { [locale]: data?.bundle ?? {} }
    return {
      locale,
      ready: !isPending,
      t: createTranslator(catalog, [locale]),
      setLocale: (next: string) => {
        setStoredLocale(next)
        location.reload()
      },
    }
  }, [data, isPending, locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) throw new Error('useI18n precisa estar dentro de <I18nProvider>')
  return context
}

/** Atalho para traduzir uma chave. Devolve a propria chave se faltar traducao. */
export function useT(): Translator {
  return useI18n().t
}
